import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Nav, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { accountApi } from '../../services/accountApi';
import { BILLING_MANAGE } from '../../auth/permissions';

const TABS = ['overview', 'plan', 'payment', 'invoices', 'usage'];

function statusBadge(status) {
  const map = {
    free: 'secondary',
    mock_active: 'success',
    active: 'success',
    pending_checkout: 'warning',
    pending_approval: 'warning',
    cancel_at_period_end: 'info',
    past_due: 'danger',
    unpaid: 'danger',
    canceled: 'dark',
  };
  return <Badge bg={map[status] || 'secondary'}>{status || '—'}</Badge>;
}

function BillingPage() {
  const { activePartnerId, hasPartnerPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const canManage = hasPartnerPermission(BILLING_MANAGE);

  const load = useCallback(async () => {
    if (!activePartnerId) return;
    setError('');
    try {
      const res = await accountApi.getPartnerBilling(activePartnerId);
      setData(res);
    } catch (e) {
      setError(e.message || 'Unable to load billing.');
    }
  }, [activePartnerId]);

  useEffect(() => {
    load();
  }, [load]);

  const setTab = (next) => setSearchParams({ tab: next });

  const startCheckout = async (tier, interval) => {
    setBusy(true);
    setError('');
    try {
      const res = await accountApi.startBillingCheckout(activePartnerId, { tier, interval });
      const url = res.checkout?.checkout_url || res.checkout_url;
      if (url && url.startsWith('/')) {
        navigate(url);
      } else {
        await load();
      }
    } catch (e) {
      setError(e.message || 'Checkout failed.');
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = await accountApi.openBillingPortal(activePartnerId);
      const url = res.portal_url;
      if (url && url.startsWith('/')) navigate(url);
    } catch (e) {
      setError(e.message || 'Unable to open billing portal.');
    } finally {
      setBusy(false);
    }
  };

  const changePlan = async (tier, interval) => {
    setBusy(true);
    try {
      await accountApi.changeBillingPlan(activePartnerId, { tier, interval });
      await load();
    } catch (e) {
      setError(e.message || 'Plan change failed.');
    } finally {
      setBusy(false);
    }
  };

  const cancelSub = async () => {
    setBusy(true);
    try {
      await accountApi.cancelBilling(activePartnerId, { at_period_end: true });
      await load();
    } catch (e) {
      setError(e.message || 'Cancel failed.');
    } finally {
      setBusy(false);
    }
  };

  const resumeSub = async () => {
    setBusy(true);
    try {
      await accountApi.resumeBilling(activePartnerId);
      await load();
    } catch (e) {
      setError(e.message || 'Resume failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!activePartnerId) {
    return <Alert variant="warning">Select a Partner workspace to manage billing.</Alert>;
  }
  if (!data && !error) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const sub = data?.subscription || {};
  const pm = data?.payment_method;

  return (
    <div className="console-page">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="h3 mb-1">Account &amp; Billing</h1>
          <p className="text-muted mb-0">
            Tools &amp; API Tier and subscription for {data?.partner?.name || 'this Partner'}.
          </p>
        </div>
        {canManage && data?.is_mock && data?.mutations_allowed && (
          <Button variant="outline-primary" size="sm" disabled={busy} onClick={openPortal}>
            Manage Billing
          </Button>
        )}
      </div>

      {data?.is_mock && (
        <Alert variant="warning">
          <strong>Mock billing</strong> — no real payment was processed. Stripe is not connected.
        </Alert>
      )}
      {error && <Alert variant="danger">{error}</Alert>}

      <Nav variant="tabs" className="mb-3">
        {TABS.map((t) => (
          <Nav.Item key={t}>
            <Nav.Link active={tab === t} onClick={() => setTab(t)} className="text-capitalize">
              {t === 'payment' ? 'Payment Method' : t}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {tab === 'overview' && (
        <Row className="g-3">
          <Col md={6}>
            <Card>
              <Card.Body>
                <Card.Title className="h6">Current Plan</Card.Title>
                <p className="mb-1">
                  <strong>{sub.selected_tier || '—'}</strong> {statusBadge(sub.billing_status)}
                </p>
                <p className="small text-muted mb-0">
                  Interval: {sub.billing_interval || 'none'}
                  {sub.current_period_end
                    ? ` · Period ends ${new Date(sub.current_period_end).toLocaleDateString()}`
                    : ''}
                </p>
                {sub.cancel_at_period_end && (
                  <Alert variant="info" className="mt-2 mb-0 py-2">
                    Cancellation scheduled at period end.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Body>
                <Card.Title className="h6">Payment Method</Card.Title>
                {pm ? (
                  <p className="mb-0">{pm.display}</p>
                ) : (
                  <p className="text-muted mb-0">
                    {sub.selected_tier === 'free_indie' ? 'Not required' : 'None on file'}
                  </p>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={12}>
            <Card>
              <Card.Body>
                <Card.Title className="h6">Latest Invoice</Card.Title>
                {data.latest_invoice ? (
                  <p className="mb-0">
                    {data.latest_invoice.invoice_number} —{' '}
                    {(data.latest_invoice.total_cents / 100).toFixed(2)}{' '}
                    {data.latest_invoice.currency?.toUpperCase()} ({data.latest_invoice.status})
                    {data.latest_invoice.is_mock ? ' · mock' : ''}
                  </p>
                ) : (
                  <p className="text-muted mb-0">No invoices yet.</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {tab === 'plan' && (
        <Row className="g-3">
          {(data.plans || []).map((plan) => (
            <Col md={4} key={plan.code}>
              <Card className={sub.selected_tier === plan.code ? 'border-primary' : ''}>
                <Card.Body>
                  <Card.Title className="h6">{plan.display_name}</Card.Title>
                  <p className="small text-muted">{plan.short_description}</p>
                  <p className="fw-semibold">{plan.display_price}</p>
                  {plan.example_pricing?.note && (
                    <p className="small text-warning">{plan.example_pricing.note}</p>
                  )}
                  {canManage && plan.code === 'pro' && sub.selected_tier === 'free_indie' && (
                    <div className="d-grid gap-2">
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => startCheckout('pro', 'monthly')}
                      >
                        Upgrade monthly
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={busy}
                        onClick={() => startCheckout('pro', 'annual')}
                      >
                        Upgrade annual
                      </Button>
                    </div>
                  )}
                  {canManage && plan.code === 'free_indie' && sub.selected_tier === 'pro' && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={busy}
                      onClick={() => changePlan('free_indie', 'none')}
                    >
                      Downgrade at period end
                    </Button>
                  )}
                  {canManage && plan.code === 'enterprise' && sub.status !== 'pending_approval' && (
                    <Button
                      size="sm"
                      variant="outline-dark"
                      disabled={busy}
                      onClick={() => changePlan('enterprise', 'custom')}
                    >
                      Request Enterprise
                    </Button>
                  )}
                  {sub.selected_tier === plan.code && <Badge bg="primary">Current</Badge>}
                </Card.Body>
              </Card>
            </Col>
          ))}
          {canManage && sub.status === 'cancel_at_period_end' && (
            <Col xs={12}>
              <Button disabled={busy} onClick={resumeSub}>
                Resume Subscription
              </Button>
            </Col>
          )}
          {canManage &&
            ['mock_active', 'active'].includes(sub.billing_status) &&
            !sub.cancel_at_period_end && (
              <Col xs={12}>
                <Button variant="outline-danger" disabled={busy} onClick={cancelSub}>
                  Cancel Subscription
                </Button>
              </Col>
            )}
        </Row>
      )}

      {tab === 'payment' && (
        <Card>
          <Card.Body>
            <Card.Title className="h6">Payment Method</Card.Title>
            {pm ? (
              <>
                <p>{pm.display}</p>
                <p className="small text-muted">
                  Expires {pm.exp_month}/{pm.exp_year} · {pm.billing_name}
                </p>
              </>
            ) : (
              <p className="text-muted">No payment method on file.</p>
            )}
            {canManage && data?.is_mock && (
              <Button as={Link} to="/console/account/billing/portal" size="sm" variant="outline-primary">
                Change in mock portal
              </Button>
            )}
          </Card.Body>
        </Card>
      )}

      {tab === 'invoices' && (
        <Table responsive hover size="sm">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Status</th>
              <th>Total</th>
              <th>Issued</th>
              <th>Provider</th>
            </tr>
          </thead>
          <tbody>
            {(data.invoices || []).length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted">
                  No invoices yet.
                </td>
              </tr>
            )}
            {(data.invoices || []).map((inv) => (
              <tr key={inv.invoice_id}>
                <td>{inv.invoice_number}</td>
                <td>{inv.status}</td>
                <td>
                  {(inv.total_cents / 100).toFixed(2)} {inv.currency?.toUpperCase()}
                </td>
                <td>{inv.issued_at ? new Date(inv.issued_at).toLocaleDateString() : '—'}</td>
                <td>{inv.is_mock ? 'mock' : inv.provider}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {tab === 'usage' && (
        <Card>
          <Card.Body>
            <Card.Title className="h6">Usage &amp; Quotas</Card.Title>
            <p className="small text-muted">
              Entitlements follow the effective Tools &amp; API Tier. See{' '}
              <Link to="/console/usage">Usage</Link> for live counters.
            </p>
            <pre className="small bg-light p-3 rounded">
              {JSON.stringify(
                { quotas: data.quotas, capabilities: data.entitlements?.capabilities },
                null,
                2
              )}
            </pre>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

export default BillingPage;
